'use client';

import {
  buildCriterionExpression,
  buildFieldSortCriterion,
  buildRelevanceSortCriterion,
  buildSort,
  Context,
  SearchEngine,
  SortCriterion,
  SortOrder,
} from '@coveo/atomic-react';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const RELEVANCE_SORT = {
  value: 'relevance',
  criterion: buildRelevanceSortCriterion(),
};
export const POPULARITY_SORT = {
  value: 'popularity',
  criterion: buildRelevanceSortCriterion(),
};

export const SEARCH_SORT_CRITERIA: {
  value: string;
  criterion: SortCriterion;
}[] = [
  RELEVANCE_SORT,
  POPULARITY_SORT,
  {
    value: 'rating',
    criterion: buildFieldSortCriterion('ec_rating_sort', SortOrder.Descending),
  },
  {
    value: 'published-date',
    criterion: buildFieldSortCriterion(
      'first_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'recently-updated',
    criterion: buildFieldSortCriterion(
      'last_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'price-low-to-high',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Ascending),
  },
  {
    value: 'price-high-to-low',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Descending),
  },
];

export const LISTING_SORT_CRITERIA: {
  value: string;
  criterion: SortCriterion;
}[] = [
  POPULARITY_SORT,
  {
    value: 'rating',
    criterion: buildFieldSortCriterion('ec_rating_sort', SortOrder.Descending),
  },
  {
    value: 'published-date',
    criterion: buildFieldSortCriterion(
      'first_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'recently-updated',
    criterion: buildFieldSortCriterion(
      'last_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'price-low-to-high',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Ascending),
  },
  {
    value: 'price-high-to-low',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Descending),
  },
];

export const getSortCriteriaForSearchHub = (searchHub: string | undefined) => {
  if (searchHub === CoveoSearchHub.SEARCH) {
    return SEARCH_SORT_CRITERIA;
  }
  return LISTING_SORT_CRITERIA;
};

export const useSortController = (
  searchEngine: SearchEngine | undefined,
  coveoContext: Context | undefined
) => {
  const activeSearchHub = searchEngine?.state.searchHub;
  const defaultSort = useMemo(() => {
    if (activeSearchHub === CoveoSearchHub.SEARCH) {
      return RELEVANCE_SORT;
    }

    // @ts-ignore
    coveoContext?.add('popularity', true);
    return POPULARITY_SORT;
  }, [coveoContext, activeSearchHub]);

  const sortController = useMemo(() => {
    return buildSort(searchEngine!, {
      initialState: { criterion: defaultSort.criterion },
    });
  }, [searchEngine, defaultSort.criterion]);
  const [_, setSortState] = useState(sortController?.state);
  // maintain our own record of current sort value since we use the same sort criterion for popularity and relevance
  // coveo only saves criterion in the state
  const [currentSortValue, setCurrentSortValue] = useState(defaultSort.value);

  const availableSortCriteria = useMemo(
    () => getSortCriteriaForSearchHub(activeSearchHub),
    [activeSearchHub]
  );

  const getCriterionFromValue = (value: string) =>
    availableSortCriteria.find((sortOption) => sortOption.value === value)
      ?.criterion ?? POPULARITY_SORT.criterion;

  const getValueFromCriterionString = useCallback(
    (criterion: string) =>
      availableSortCriteria.find(
        (sortOption) =>
          buildCriterionExpression(sortOption.criterion) === criterion
      )?.value ?? POPULARITY_SORT.value,
    [availableSortCriteria]
  );

  useEffect(
    () =>
      sortController?.subscribe(() => {
        setSortState(sortController.state);

        if (sortController.state.sortCriteria !== 'relevancy') {
          // this is here for the initial load, if theres a URL sort param specified then coveo headless will trigger this update
          // we need to synchronize our manual state. We don't if its relevancy since we've already set the custom value to either relevance or popularity,
          // and getting the value from the criterion here can't tell the difference between the two since they both use relevancy
          setCurrentSortValue(
            getValueFromCriterionString(sortController.state.sortCriteria)
          );

          // popularity is added by default when there is no query, remove it if we're sorting by something else
          // @ts-ignore
          coveoContext?.add('popularity', false);
        }
      }),
    [
      sortController,
      coveoContext,
      currentSortValue,
      getValueFromCriterionString,
    ]
  );

  return {
    currentSortValue,
    availableSortCriteria,
    setSort: (sortValue: string) => {
      if (sortValue === 'popularity') {
        // ContextValue is setup to string only in TS, but in the Coveo dashboard it can be string or boolean.
        // Can ignore the TS error and it seems to work fine as boolean, and properly triggers our boolean Condition
        // @ts-ignore
        coveoContext?.add('popularity', true);
      } else {
        // @ts-ignore
        coveoContext?.add('popularity', false);
      }

      sortController.sortBy(getCriterionFromValue(sortValue));
      setCurrentSortValue(sortValue);
    },
  };
};
